// Bibliothèque d'assets Kenney (CC0) pour LaCaze Life.
//
// DEUX usages :
//  1. `KENNEY` : catalogue CURÉ et typé des assets les plus utiles (tilesets, persos, UI, emotes,
//     fonts, sons) avec leur chemin + config de frame. À référencer directement dans le code/les data.
//  2. `searchAssets()` : recherche dans le catalogue COMPLET (game/public/assets/asset-index.json,
//     ~4800 assets) chargé à la demande — pour quand on cherche un asset précis non curé.
//
// Tous les chemins sont servis sous `/assets/kenney/...` (dossier game/public). Tuiles = 16×16.
// Ne JAMAIS modifier les originaux : mettre les variantes dans game/public/assets/custom/.
import 'phaser'

export interface SheetRef {
    path: string
    frameWidth: number
    frameHeight: number
    margin: number
    spacing: number
}
export interface ImageRef {
    path: string
}

const K = '/assets/kenney'

// --- Catalogue curé (les incontournables, 16×16) ---
export const KENNEY = {
    /** Tilesets (spritesheets 16×16). `packed` = sans marge ; les roguelike ont 1px d'espacement. */
    tilesets: {
        // Extérieur : herbe, chemins, arbres, maisons, clôtures (base de la carte campus).
        tinyTown: sheet(`${K}/tier1-visual/tiny-town/Tilemap/tilemap_packed.png`, 0, 0),
        // Urbain moderne : béton, immeubles, routes, mobilier urbain (Rue du Collège, extérieurs).
        modernCity: sheet(`${K}/tier1-visual/roguelike-modern-city/Tilemap/tilemap_packed.png`, 0, 0),
        // Intérieurs : sols, murs, meubles (salles, CDI, cantine, infirmerie, admin).
        indoors: sheet(`${K}/tier1-visual/roguelike-indoors/Tilesheets/roguelikeIndoor_transparent.png`, 0, 1),
        // RPG général : déco, objets, items divers.
        rpg: sheet(`${K}/tier2-visual/roguelike-rpg-pack/Spritesheet/roguelikeSheet_transparent.png`, 0, 1),
    },
    /** Spritesheets de personnages (base des Kazis, 16×16). */
    characters: {
        roguelike: sheet(`${K}/tier1-visual/roguelike-characters/Spritesheet/roguelikeChar_transparent.png`, 0, 1),
    },
    /** UI : feuille complète + panneaux 9-slice (HUD, panneaux de stats, menus). */
    ui: {
        sheet: img(`${K}/tier1-visual/pixel-ui-pack/Spritesheet/UIpackSheet_transparent.png`),
        panelGrey: img(`${K}/tier1-visual/pixel-ui-pack/9-Slice/Colored/grey.png`),
        panelBrown: img(`${K}/tier1-visual/pixel-ui-pack/9-Slice/Colored/brown.png`),
    },
    /** Emotes 16×16 (bulles d'humeur au-dessus des Kazis). Cf. kenneyEmote(name). */
    emotes: {
        dir: `${K}/tier1-visual/emotes-pack/PNG/Pixel/Style 1`,
        // noms réels du pack ; liste complète via searchAssets({pack:'emotes-pack'})
        names: ['faceHappy', 'faceSad', 'faceAngry', 'anger', 'heart', 'heartBroken', 'star',
            'exclamation', 'question', 'laugh', 'idea', 'sleep', 'music', 'drop'] as const,
    },
    /** Polices (TTF). Charger via loadKenneyFont() puis utiliser la famille dans un style de texte. */
    fonts: {
        pixel: { family: 'KenneyPixel', path: `${K}/tier1-visual/kenney-fonts/Fonts/Kenney Pixel.ttf` },
        mini: { family: 'KenneyMini', path: `${K}/tier1-visual/kenney-fonts/Fonts/Kenney Mini.ttf` },
    },
    /** Sons (ogg). Sous-ensemble curé ; le reste via searchAssets({kind:'audio'}). */
    audio: {
        uiClick: `${K}/tier3-audio/ui-audio/Audio/click1.ogg`,
        doorOpen: `${K}/tier3-audio/rpg-audio/Audio/doorOpen_1.ogg`,
        bookFlip: `${K}/tier3-audio/rpg-audio/Audio/bookFlip1.ogg`,
    },
} as const

function sheet(path: string, margin: number, spacing: number): SheetRef {
    return { path, frameWidth: 16, frameHeight: 16, margin, spacing }
}
function img(path: string): ImageRef {
    return { path }
}

/** Chemin d'un emote par nom (ex: kenneyEmote('happy')). */
export function kenneyEmote(name: string): string {
    return `${KENNEY.emotes.dir}/emote_${name}.png`
}

// --- Helpers de chargement Phaser (à appeler dans preload) ---

/** Charge un tileset/persos Kenney comme spritesheet (frames 16×16). */
export function loadKenneySheet(scene: Phaser.Scene, key: string, ref: SheetRef): void {
    if (scene.textures.exists(key)) return
    scene.load.spritesheet(key, ref.path, {
        frameWidth: ref.frameWidth,
        frameHeight: ref.frameHeight,
        margin: ref.margin,
        spacing: ref.spacing,
    })
}

/** Charge une image Kenney simple. `path` accepte un ImageRef ou un chemin servi. */
export function loadKenneyImage(scene: Phaser.Scene, key: string, path: string | ImageRef): void {
    if (scene.textures.exists(key)) return
    scene.load.image(key, typeof path === 'string' ? path : path.path)
}

/** Charge un son Kenney. */
export function loadKenneyAudio(scene: Phaser.Scene, key: string, path: string): void {
    if (scene.cache.audio.exists(key)) return
    scene.load.audio(key, path)
}

/** Charge une police TTF (FontFace) et l'enregistre — à await avant de l'utiliser dans un texte. */
export async function loadKenneyFont(family: string, path: string): Promise<void> {
    if ((document as any).fonts?.check?.(`12px "${family}"`)) return
    const face = new FontFace(family, `url("${encodeURI(path)}")`)
    await face.load()
    ;(document as any).fonts.add(face)
}

// --- Recherche dans le catalogue complet (chargé à la demande, hors bundle) ---

export interface AssetEntry {
    id: string
    pack: string
    tier: string
    kind: string
    path: string
    ext: string
    size: number
    w?: number
    h?: number
}
interface AssetIndex {
    totals: { packs: number; assets: number }
    packs: Array<{ slug: string; tier: string; usableAssets: number; kinds: Record<string, number> }>
    assets: AssetEntry[]
}

let indexPromise: Promise<AssetIndex> | null = null
/** Charge (et met en cache) le catalogue complet depuis /assets/asset-index.json. */
export function loadAssetIndex(): Promise<AssetIndex> {
    if (!indexPromise) {
        indexPromise = fetch('/assets/asset-index.json').then(r => r.json())
    }
    return indexPromise
}

export interface AssetQuery {
    /** sous-chaîne recherchée dans le chemin (insensible à la casse) */
    query?: string
    pack?: string
    kind?: string
    tier?: string
    limit?: number
}
/** Recherche des assets dans le catalogue complet. Ex: searchAssets({ pack:'tiny-town', query:'tree' }). */
export async function searchAssets(q: AssetQuery = {}): Promise<AssetEntry[]> {
    const idx = await loadAssetIndex()
    const needle = q.query?.toLowerCase()
    let out = idx.assets.filter(a =>
        (!q.pack || a.pack === q.pack) &&
        (!q.kind || a.kind === q.kind) &&
        (!q.tier || a.tier === q.tier) &&
        (!needle || a.path.toLowerCase().includes(needle))
    )
    if (q.limit) out = out.slice(0, q.limit)
    return out
}
